import React from "react";
import { Menu, X, LogOut, Settings } from "lucide-react";
import Button from "./Button";
import { getTierKey } from "../../config/themes";
import { getInstallPrompt, canShowInstall } from "./InstallAppBanner";
import { useUnreadLeads } from "../../hooks/UnreadLeadsContext";
import { MARKETPLACE_ENABLED } from "../../featureFlags";

// ── Business logo circle with initials fallback ────────────────────────────────
function BusinessLogo({ logoUrl, name, size, border, boxShadow }) {
  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const baseStyle = {
    width: size,
    height: size,
    borderRadius: "50%",
    border,
    boxShadow,
    flexShrink: 0,
    overflow: "hidden",
  };

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name || "Business"}
        style={{ ...baseStyle, objectFit: "cover", display: "block" }}
      />
    );
  }

  return (
    <div
      style={{
        ...baseStyle,
        background: "#1A4080",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: Math.round(size * 0.35) + "px",
        fontWeight: 700,
        color: "#fff",
        letterSpacing: "0.02em",
      }}
    >
      {initials}
    </div>
  );
}

// ── Tier badge pill ────────────────────────────────────────────────────────────
function TierBadge({ label, bg, color, border }) {
  return (
    <span
      className="tier-pill"
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: bg,
        color,
        border: border || "none",
        borderRadius: "999px",
        padding: "2px 8px",
        fontSize: "0.65rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        lineHeight: 1.5,
        flexShrink: 0,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ── Header styles per tier ─────────────────────────────────────────────────────
const TIER_STYLES = {
  free: {
    background: "var(--dark)",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
  },
  trial: {
    background: "var(--dark)",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
  },
  pro: {
    background: "linear-gradient(135deg, #0F1F3D 0%, #162943 100%)",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
  },
  ultimate: {
    background: "linear-gradient(135deg, #1B1F2B 0%, #2A2F3D 50%, #1B1F2B 100%)",
    borderBottom: "1px solid rgba(148,163,184,0.2)",
    boxShadow: "0 1px 0 rgba(148,163,184,0.15) inset",
  },
  enterprise: {
    background: "#0A0A0A",
    borderBottom: "1px solid rgba(201,168,76,0.25)",
  },
};

export default function Header({
  currentView,
  setCurrentView,
  mobileOpen,
  setMobileOpen,
  onSignOut,
  role,
  subscription,
  profile,
  onShowPricing,
  creditBalance,
  theme,
}) {
  const { unreadCount } = useUnreadLeads();
  const badgeLabel = unreadCount > 9 ? "9+" : String(unreadCount);
  const isTradie = role === "tradie";
  const isCustomer = role === "customer";
  const isLoggedOut = !role;
  const showSettings = isTradie;

  const tier = isTradie ? getTierKey(subscription, theme?.id) : "free";

  // (Theme-aware icon swap removed 2026-04-29 — the new transparent logo
  //  works on both light and dark backgrounds, so isDark + its observer
  //  are no longer needed.)

  const businessName = profile?.business_name || profile?.full_name || "";
  const logoUrl = profile?.logo_url || null;
  const isSimulating = !!(subscription?.simulatedTierId);

  // Gold "SIMULATING" badge shown when tier simulation is active
  const SimBadge = isSimulating ? (
    <span style={{
      display: "inline-flex", alignItems: "center",
      background: "rgba(201,168,76,0.2)", color: "#C9A84C",
      border: "1px solid rgba(201,168,76,0.4)",
      borderRadius: "999px", padding: "2px 7px",
      fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.08em",
      flexShrink: 0, whiteSpace: "nowrap",
    }}>
      SIMULATING
    </span>
  ) : null;

  // ── Brand section ────────────────────────────────────────────────────────────
  function renderBrand() {
    // ENTERPRISE: business logo + name + gold badge
    if (tier === "enterprise" && isTradie) {
      return (
        <div className="brand" onClick={() => { setCurrentView(isTradie ? "tradie" : isCustomer ? "customer" : "landing"); setMobileOpen(false); }} style={{ cursor: "pointer" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <BusinessLogo
              logoUrl={logoUrl}
              name={businessName}
              size={48}
              border="2px solid #C9A84C"
              boxShadow="0 0 16px rgba(201,168,76,0.3)"
            />
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{
                  color: "#fff", fontSize: "1.05rem", fontWeight: 700,
                  maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {businessName || "My Business"}
                </span>
                <TierBadge
                  label="ENTERPRISE"
                  bg="rgba(201,168,76,0.1)"
                  color="#C9A84C"
                  border="1px solid rgba(201,168,76,0.3)"
                />
                {SimBadge}
              </div>
              <span style={{ color: "rgba(201,168,76,0.6)", fontSize: "0.7rem", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                Remote Quote Enterprise
              </span>
            </div>
          </div>
        </div>
      );
    }

    // ALL OTHER TIERS (free, trial, pro, ultimate, logged out, customer): RemoteQuote wordmark
    const tierConfig = {
      free:     { accent: "var(--primary)", suffix: " Tradie",   badgeLabel: "FREE",     badgeBg: "rgba(255,255,255,0.1)",   badgeColor: "#ffffff",  badgeBorder: undefined },
      trial:    { accent: "#F59E0B", suffix: " Trial",    badgeLabel: "TRIAL",    badgeBg: "rgba(245,158,11,0.2)",    badgeColor: "#F59E0B",  badgeBorder: "1px solid rgba(245,158,11,0.3)" },
      pro:      { accent: "#3B82F6", suffix: " Pro",      badgeLabel: "PRO",      badgeBg: "rgba(59,130,246,0.2)",    badgeColor: "#93C5FD",  badgeBorder: "1px solid rgba(59,130,246,0.3)" },
      ultimate: { accent: "#94A3B8", suffix: " Ultimate", badgeLabel: "ULTIMATE", badgeBg: "rgba(148,163,184,0.15)",  badgeColor: "#CBD5E1",  badgeBorder: "1px solid rgba(148,163,184,0.35)" },
    };

    const config = tierConfig[tier] || tierConfig.free;
    const showBadge = isTradie;
    const suffixText = isTradie ? config.suffix : "";

    return (
      <div className="brand" onClick={() => { setCurrentView(isTradie ? "tradie" : isCustomer ? "customer" : "landing"); setMobileOpen(false); }} style={{ cursor: "pointer" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <img
            src="/remotequote-icon-transparent.png"
            alt="Remote Quote"
            style={{ height: "72px", width: "auto" }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "nowrap" }}>
            <span style={{ color: "#fff", fontSize: "1.35rem", fontWeight: 600, letterSpacing: "-0.3px", whiteSpace: "nowrap" }}>
              Remote<span style={{ color: config.accent }}>Quote</span>
              {suffixText && <span className="tier-suffix" style={{ color: "#fff" }}>{suffixText}</span>}
            </span>
            {showBadge && (
              <>
                <TierBadge
                  label={config.badgeLabel}
                  bg={config.badgeBg}
                  color={config.badgeColor}
                  border={config.badgeBorder}
                />
                {SimBadge}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Mobile brand header for ENTERPRISE only ───────────────────────────────────
  function renderMobileBrandHeader() {
    if (!isTradie || tier !== "enterprise") return null;

    return (
      <div style={{
        display: "flex", alignItems: "center", gap: "10px",
        padding: "12px 16px 10px",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        marginBottom: "8px",
      }}>
        <BusinessLogo logoUrl={logoUrl} name={businessName} size={36} border="2px solid #C9A84C" />
        <div>
          <div style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem", marginBottom: "3px" }}>
            {businessName || "My Business"}
          </div>
          <TierBadge label="ENTERPRISE" bg="rgba(201,168,76,0.1)" color="#C9A84C" border="1px solid rgba(201,168,76,0.3)" />
        </div>
      </div>
    );
  }

  // ── Nav buttons ──────────────────────────────────────────────────────────────
  function navButtons(mobile = false) {
    const close = () => mobile && setMobileOpen(false);
    return (
      <>
        {!isTradie && (
          <Button
            secondary={currentView !== "landing"}
            onClick={() => { setCurrentView("landing"); close(); }}
          >
            Overview
          </Button>
        )}

        {isLoggedOut && (
          <Button secondary onClick={() => { setCurrentView("customer-auth"); close(); }}>
            Customer login
          </Button>
        )}
        {isLoggedOut && (
          <Button secondary onClick={() => { setCurrentView("tradie"); close(); }}>
            Tradie login
          </Button>
        )}
        {isCustomer && (
          <Button secondary={currentView !== "customer"} onClick={() => { setCurrentView("customer"); close(); }}>
            Customer dashboard
          </Button>
        )}
        {isCustomer && (
          <Button secondary={currentView !== "customer-messages"} onClick={() => { setCurrentView("customer-messages"); close(); }}>
            💬 Messages{unreadCount > 0 ? ` (${unreadCount})` : ""}
          </Button>
        )}
        {isCustomer && (
          <Button secondary={currentView !== "customer-settings"} onClick={() => { setCurrentView("customer-settings"); close(); }}>
            <Settings size={16} /> Settings
          </Button>
        )}
        {isTradie && (
          <Button secondary={currentView !== "tradie"} onClick={() => { setCurrentView("tradie"); close(); }}>
            Home
          </Button>
        )}
        {showSettings && (
          <Button secondary={currentView !== "settings"} onClick={() => { setCurrentView("settings"); close(); }}>
            <Settings size={16} /> Settings
          </Button>
        )}
        {canShowInstall() && (
          <Button secondary onClick={async () => {
            const prompt = getInstallPrompt();
            if (prompt) {
              try {
                prompt.prompt();
                await prompt.userChoice;
              } catch {}
            } else {
              const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
              if (isIOS) {
                close();
                setTimeout(() => window.alert("To install: tap the Share button ↗ then 'Add to Home Screen'"), 100);
                return;
              } else {
                close();
                setTimeout(() => window.alert("To install: tap the browser menu ⋮ then 'Install app'"), 100);
                return;
              }
            }
            close();
          }}>
            📲 Install app
          </Button>
        )}
        {onSignOut && (
          <Button secondary onClick={() => { onSignOut(); close(); }}>
            <LogOut size={16} /> Sign out
          </Button>
        )}
      </>
    );
  }

  // Prefer theme values supplied by useTheme; fall back to the static TIER_STYLES map.
  const headerStyle = theme
    ? {
        background: theme.headerBg,
        borderBottom: theme.headerBorder,
        ...(theme.headerBoxShadow ? { boxShadow: theme.headerBoxShadow } : {}),
      }
    : TIER_STYLES[tier];

  return (
    <div
      className="header"
      style={{ ...headerStyle, position: "sticky" }}
      data-tier={tier}
    >
      {/* TRIAL: animated gradient top strip — colour driven by theme */}
      {tier === "trial" && (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: "3px",
          background: theme?.trialStripeGradient || "linear-gradient(90deg, #D97706, #F59E0B, #FBBF24, #F59E0B, #D97706)",
          backgroundSize: "200% 100%",
          animation: "slideGradient 2s linear infinite",
          pointerEvents: "none",
        }} />
      )}

      {/* ENTERPRISE: static gold gradient top strip */}
      {tier === "enterprise" && (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: "2px",
          background: "linear-gradient(90deg, transparent, #C9A84C, transparent)",
          pointerEvents: "none",
        }} />
      )}

      <div className="header-inner">
        {renderBrand()}

        <div className="desktop-nav">
          {/* ULTIMATE: credits pill in the nav area */}
          {MARKETPLACE_ENABLED && tier === "ultimate" && isTradie && creditBalance !== undefined && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: "4px",
              background: "rgba(148,163,184,0.1)",
              color: "#CBD5E1",
              border: "1px solid rgba(148,163,184,0.2)",
              borderRadius: "999px",
              padding: "5px 12px",
              fontSize: "0.8rem",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}>
              ⚡ {creditBalance} credits
            </span>
          )}

          {navButtons(false)}
        </div>

        <button
          type="button"
          className="icon-btn mobile-only"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={
            mobileOpen
              ? "Close menu"
              : unreadCount > 0
              ? `Open menu — ${unreadCount} unread`
              : "Open menu"
          }
          style={{ position: "relative" }}
        >
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          {!mobileOpen && unreadCount > 0 && (
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                top: "-4px",
                right: "-4px",
                minWidth: "16px",
                height: "16px",
                padding: "0 3px",
                borderRadius: "999px",
                background: "#DC2626",
                color: "#fff",
                fontSize: "10px",
                fontWeight: 600,
                lineHeight: "16px",
                textAlign: "center",
                boxSizing: "border-box",
              }}
            >
              {badgeLabel}
            </span>
          )}
        </button>
      </div>

      {/* TRIAL: days-remaining sub-bar — hidden on mobile via CSS */}
      {tier === "trial" && (
        <div className="trial-sub-bar" style={{ padding: "4px 28px", background: "rgba(245,158,11,0.08)" }}>
          <span style={{ fontSize: "0.8rem", color: "#F59E0B" }}>
            ⏳ {subscription?.trialDaysRemaining ?? 0} days left in your trial
            {" · "}
            <button
              type="button"
              onClick={onShowPricing}
              style={{
                background: "none", border: "none", color: "#F59E0B",
                fontSize: "0.8rem", cursor: "pointer", textDecoration: "underline", padding: 0,
              }}
            >
              Upgrade now
            </button>
          </span>
        </div>
      )}

      {/* EXPIRED TRIAL: distinct urgent sub-bar */}
      {subscription?.isTrialExpired && tier !== "trial" && (
        <div
          className="trial-sub-bar"
          style={{ padding: "6px 28px", background: "rgba(239,68,68,0.08)", borderTop: "1px solid rgba(239,68,68,0.15)" }}
        >
          <span style={{ fontSize: "0.8rem", color: "#EF4444", fontWeight: 600 }}>
            ⚠️ Your free trial has ended — you're on the Free plan.
            {" · "}
            <button
              type="button"
              onClick={onShowPricing}
              style={{
                background: "none", border: "none", color: "#EF4444",
                fontSize: "0.8rem", cursor: "pointer", textDecoration: "underline", padding: 0,
                fontWeight: 600,
              }}
            >
              Upgrade to restore access
            </button>
          </span>
        </div>
      )}

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="mobile-nav">
          {renderMobileBrandHeader()}
          {navButtons(true)}
        </div>
      )}
    </div>
  );
}
